import { useState } from 'react';
import { Alert, Box, Tab, Tabs, Typography } from '@mui/material';
import { PersonSearchRounded } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { credentialProgressApi } from '../../../lib/api/credential-progress.api';
import { errorReason, errorText } from '../../../lib/error-text';
import { useTenantStore } from '../../../stores/tenant.store';
import { AcademicRecordTab } from '../../sis/components/academic-record/AcademicRecordTab';
import { CredentialProgressTab } from '../../sis/components/academic-record/CredentialProgressTab';
import { PortalEmpty, PortalLoading } from '../PortalShell';
import { usePortalCacheScope } from '../../../stores/portal-view.store';

// ─── El expediente del alumno, en su portal (graduation_audit) ──────────────
// Las MISMAS pestañas que ve el personal en la ficha (Expediente y Avance de la
// credencial), con el mismo cálculo y el mismo PDF: el alumno y el registro no
// pueden ver números distintos.
//
// SIN `personId`: el servidor deduce la persona de la sesión
// (`credentialProgressApi.getOwn`). Si la cuenta no tiene persona vinculada, se
// explica con palabras, no con un error técnico.

export function PortalRecordPage(): JSX.Element {
  const { t } = useTranslation('sis');
  const { currentTenantId } = useTenantStore();
  const [tab, setTab] = useState(0);
  const scope = usePortalCacheScope();
  const recordQ = useQuery({
    // Con la vista en la clave: lo del alumno mirado nunca se cruza con la caché propia.
    queryKey: ['portal-record', currentTenantId, scope],
    enabled: !!currentTenantId,
    queryFn: () => credentialProgressApi.getOwn({ tenantId: currentTenantId! }),
    staleTime: 60_000,
    retry: false,
  });

  if (recordQ.isLoading) return <PortalLoading />;
  if (recordQ.isError) {
    if (errorReason(recordQ.error) === 'record.no_person_for_account') {
      return (
        <PortalEmpty
          icon={<PersonSearchRounded fontSize="inherit" />}
          title={t('portal.record.noPersonTitle')}
          body={t('portal.record.noPersonBody')}
        />
      );
    }
    return <Alert severity="error">{errorText(recordQ.error)}</Alert>;
  }
  const progress = recordQ.data!;

  return (
    <Box>
      <Typography variant="h4" fontWeight={700} color="#e6edf3">
        {t('portal.record.title')}
      </Typography>
      <Typography variant="body2" color="#8b949e" mb={2}>
        {t('portal.record.subtitle')}
      </Typography>
      <Tabs value={tab} onChange={(_, v: number) => setTab(v)} sx={{ mb: 3 }}>
        <Tab label={t('academicRecord.tabs.record')} />
        <Tab label={t('academicRecord.tabs.credential')} />
      </Tabs>
      {tab === 0 ? (
        <AcademicRecordTab progress={progress} audience="student" />
      ) : (
        <CredentialProgressTab progress={progress} audience="student" />
      )}
    </Box>
  );
}
